import { Vector3 } from "@babylonjs/core";
import type { MallStats, Shop, ShopProduct } from "@mall-mart/shared";
import { getMallStats, refreshMallStats } from "./api/statsApi.js";
import { loadConfig, ClientConfig } from "./config/index.js";
import { ColyseusClient } from "./network/ColyseusClient.js";
import { StateSync } from "./network/StateSync.js";
import { MallScene } from "./world/MallScene.js";
import { MallLighting, setupMallLighting } from "./world/lighting.js";
import { addAtriumFloorReflection } from "./world/mirrorFloor.js";
import { PropLibrary } from "./world/propLibrary.js";
import { XRManager } from "./world/XRManager.js";
import { AtriumZone } from "./world/zones/AtriumZone.js";
import { WingZone } from "./world/zones/WingZone.js";
import { FoodCourtZone } from "./world/zones/FoodCourtZone.js";
import { ShopUnitZone } from "./world/zones/ShopUnitZone.js";
import { computeMallLayout, MallLayout, WingName } from "@mall-mart/shared";
import { StreamingManager } from "./world/StreamingManager.js";
import { WingPrefetcher } from "./world/wingPrefetch.js";
import { LocalPlayer } from "./player/LocalPlayer.js";
import { HUD } from "./ui/HUD.js";
import { Splash } from "./ui/Splash.js";
import { ProductPanel } from "./ui/ProductPanel.js";
import { CartPanel } from "./ui/CartPanel.js";
import { MallStatsPanel } from "./ui/MallStatsPanel.js";
import { CartManager } from "./commerce/CartManager.js";
import { installExitGuard } from "./commerce/exitGuard.js";
import type { Cart } from "./commerce/Cart.js";
import { fetchShops, fetchProducts } from "./api/shopsApi.js";
import { preloadAllProducts } from "./api/productPreload.js";
import { preloadImageUrls } from "./api/imagePreload.js";
import { SPAWN_POSE } from "@mall-mart/shared";
import { resizeShopifyImage } from "./world/imageUtils.js";
import { TouchJoystick } from "./input/TouchJoystick.js";
import { TouchLook } from "./input/TouchLook.js";
import { JumpButton } from "./input/JumpButton.js";
import { LoginScreen, LoginChoice } from "./ui/LoginScreen.js";
import {
  Session,
  loginWithGoogle,
  loginWithPassword,
  validateStoredSession,
  clearSession,
} from "./auth/session.js";
import { restorePersistentCart, startCartAutosave } from "./commerce/cartSync.js";
import { startVersionWatch } from "./versionWatch.js";

function httpBase(serverWsUrl: string): string {
  return serverWsUrl.replace(/^ws(s)?:/, "http$1:");
}

async function main(): Promise<void> {
  // Studio splash plays over the world while it loads; not awaited so the
  // mall builds behind it.
  const splash: Splash = new Splash("splash");
  void splash.play();

  const hud: HUD = new HUD("hud");
  const config: ClientConfig = loadConfig();

  const canvas: HTMLCanvasElement | null = document.getElementById("renderCanvas") as HTMLCanvasElement;
  if (canvas === null) {
    throw new Error("#renderCanvas not found");
  }

  hud.setStatus("Loading store directory…");
  const shops: Shop[] = await fetchShops(config.SERVER_URL);

  // Identity resolves CONCURRENTLY with the world build below: the sign-in
  // overlay (or silent re-login) runs while the splash video plays and the
  // mall loads behind it, so no loading time is wasted on the login screen.
  // Auth failures degrade to guest; the mall never blocks on login.
  const login: LoginScreen = new LoginScreen("login", config.GOOGLE_CLIENT_ID);
  const sessionPromise: Promise<Session | null> = (async (): Promise<Session | null> => {
    let s: Session | null = await validateStoredSession(httpBase(config.SERVER_URL));
    let overlayMessage: string | undefined;
    while (s === null) {
      const choice: LoginChoice = await login.choose(overlayMessage);
      if (choice.kind === "guest") {
        break;
      }
      try {
        s =
          choice.kind === "google"
            ? await loginWithGoogle(httpBase(config.SERVER_URL), choice.googleIdToken)
            : await loginWithPassword(httpBase(config.SERVER_URL), choice.email, choice.password);
      } catch (err: unknown) {
        if (err instanceof Error && err.message === "EMAIL_VERIFICATION_REQUIRED") {
          // Account exists (or was just created) and the verification email
          // is in their inbox; loop back to the sign-in screen.
          overlayMessage =
            "Almost there! We emailed you a verification link — click it, then sign in again.";
          continue;
        }
        if (err instanceof Error && err.message === "SIGNIN_REJECTED") {
          overlayMessage = "Invalid email or password — try again.";
          continue;
        }
        console.error("[Mall Mart] Sign-in failed; continuing as guest:", err);
        hud.setStatus("Sign-in unavailable — continuing as guest");
        break;
      }
    }
    return s;
  })();

  const carts: CartManager = new CartManager(httpBase(config.SERVER_URL), new Map());
  carts.onChange(() => {
    const active: Cart | undefined = carts.activeCart();
    const total = active?.totalPrice();
    hud.setCart(carts.totalCount(), total === undefined ? "" : `${total.amount} ${total.currency}`);
  });
  // "Leave site?" confirm when closing the tab with items still in a cart.
  installExitGuard(window, () => carts.totalCount());

  // ProductPanel's add callback has no shop context of its own; track the shop
  // whose product is currently open.
  let openShop: Shop | undefined;
  const productPanel: ProductPanel = new ProductPanel("product-panel", (product, variant) => {
    if (openShop !== undefined) {
      carts.cartFor(openShop).add(product, variant);
    }
  });

  const mall: MallScene = new MallScene(canvas);
  // IBL + fill + skylight sun. Shadow casters/receivers are registered after
  // the whole static world is built (applyToWorld below).
  const lighting: MallLighting = setupMallLighting(mall.scene, { iblUrl: "/env/mall.env" });

  const cartPanel: CartPanel = new CartPanel("cart-panel", carts, (shopId: string) =>
    fetchProducts(config.SERVER_URL, shopId)
  );
  const statsPanel: MallStatsPanel = new MallStatsPanel("stats-panel", "stats-button", getMallStats);
  mall.setUiOpenCheck(() => productPanel.isOpen() || cartPanel.isOpen() || statsPanel.isOpen());
  hud.setOnCartClick(() => cartPanel.toggle());
  document.addEventListener("keydown", (e: KeyboardEvent) => {
    const tag: string = (e.target as HTMLElement | null)?.tagName ?? "";
    if (tag === "INPUT" || tag === "TEXTAREA") {
      return;
    }
    if (e.key.toLowerCase() === "c") {
      mall.releasePointerLock();
      cartPanel.toggle();
    }
    if (e.key.toLowerCase() === "m") {
      mall.releasePointerLock();
      statsPanel.toggle();
    }
  });

  hud.setStatus("Building Mall Mart…");
  const layout: MallLayout = computeMallLayout(shops);

  // Photoscanned props (plants, food court chairs) must be in the library
  // BEFORE zones build: builders check availability synchronously and fall
  // back to procedural fixtures when a model is missing.
  await PropLibrary.for(mall.scene).preload();

  const atrium: AtriumZone = new AtriumZone(mall.scene, layout);
  await atrium.build();
  mall.controller.setConveyors(atrium.escalators);

  // Live mall stats: feed the jumbotron/thermometer/giving wall now and
  // every few minutes so "just sold" stays current.
  const pedestalShops: (Shop | undefined)[] = [];
  const updatePedestalSoldSigns = (stats: MallStats): void => {
    pedestalShops.forEach((shop, i) => {
      if (shop === undefined) {
        return;
      }
      const unitsSold: number | undefined = stats.bestSellersByShop[shop.id]?.unitsSold;
      if (unitsSold !== undefined) {
        atrium.holoPedestals[i]?.setSoldCount(unitsSold);
      }
    });
  };
  const refreshStatsDisplays = async (): Promise<void> => {
    const stats: MallStats | null = await refreshMallStats(config.SERVER_URL);
    if (stats !== null) {
      atrium.statsDisplays?.updateStats(stats);
      updatePedestalSoldSigns(stats);
    }
  };
  void refreshStatsDisplays();
  setInterval(() => {
    void refreshStatsDisplays();
  }, 5 * 60_000);

  const wingZones: WingZone[] = [];
  const shopUnits: ShopUnitZone[] = [];
  const onProductClick = (shop: Shop, product: ShopProduct): void => {
    openShop = shop;
    carts.setActive(shop.id);
    mall.releasePointerLock(); // panel needs a visible cursor
    productPanel.open(product, getMallStats()?.unitsSoldByShop[shop.id] ?? {});
  };
  const onCheckout = (shop: Shop): void => {
    carts.setActive(shop.id);
    mall.releasePointerLock();
    cartPanel.open();
  };

  for (const wingName of Object.keys(layout.wings) as WingName[]) {
    const wing: WingZone = new WingZone(mall.scene, wingName, layout.wingTitles[wingName]);
    await wing.build();
    wingZones.push(wing);
    for (const placement of layout.wings[wingName]) {
      const unit: ShopUnitZone = new ShopUnitZone(
        mall.scene,
        new Vector3(placement.x, placement.y, placement.z),
        placement.rotationY,
        placement.shop,
        config.SERVER_URL,
        onProductClick,
        onCheckout
      );
      await unit.build();
      shopUnits.push(unit);
    }
  }

  const foodCourt: FoodCourtZone = new FoodCourtZone(mall.scene);
  await foodCourt.build();

  // Every zone (and its merged shells) exists now: register shadow
  // casters/receivers and render the static shadow map its one time.
  lighting.applyToWorld();
  // Desktop-only planar reflection on the atrium floor (phones keep IBL gloss).
  addAtriumFloorReflection(mall.scene);

  // Feature each pedestal with a shop's #1 mart-score product (fetchProducts
  // returns bestseller-sorted lists). Anchor store on one, next shop on the
  // other. Fire-and-forget: pedestals keep their placeholder on any failure.
  const anchor: Shop | undefined = shops.find((s) => s.id === "anointed-attire") ?? shops[0];
  const second: Shop | undefined = shops.find((s) => s.id !== anchor?.id);
  [anchor, second].forEach((shop, i) => {
    const pedestal = atrium.holoPedestals[i];
    if (shop === undefined || pedestal === undefined) {
      return;
    }
    pedestalShops[i] = shop;
    void fetchProducts(config.SERVER_URL, shop.id)
      .then((products: ShopProduct[]) => {
        const top: ShopProduct | undefined = products[0];
        if (top === undefined) {
          return;
        }
        const priceText: string =
          top.priceRange.min === top.priceRange.max
            ? `$${top.priceRange.min}`
            : `$${top.priceRange.min} to $${top.priceRange.max}`;
        const rawImage: string | undefined = top.images[0]?.url;
        pedestal.setFeatured(
          {
            imageUrl: rawImage === undefined ? undefined : resizeShopifyImage(rawImage, 512),
            title: top.title,
            priceText,
          },
          () => onProductClick(shop, top)
        );
      })
      .catch((err: Error) => {
        console.warn(`[Mall Mart] Featured pedestal for ${shop.id} failed:`, err.message);
      });
  });
  // Stats may already have landed before the pedestals were wired up.
  const cachedStats: MallStats | null = getMallStats();
  if (cachedStats !== null) {
    updatePedestalSoldSigns(cachedStats);
  }

  const streaming: StreamingManager = new StreamingManager(
    shopUnits,
    () => mall.controller.feet()
  );

  // Warm every store in a wing the moment the player enters it, so nearby
  // interiors stream from the client cache instead of cold fetches.
  const wingShopIds = { north: [] as string[], east: [] as string[], west: [] as string[] };
  for (const wingName of Object.keys(layout.wings) as WingName[]) {
    for (const placement of layout.wings[wingName]) {
      if (placement.shop !== null) {
        wingShopIds[wingName].push(placement.shop.id);
      }
    }
  }
  const prefetcher: WingPrefetcher = new WingPrefetcher(
    wingShopIds,
    (shopId: string) => fetchProducts(config.SERVER_URL, shopId)
  );

  // Item preload: pull every store's catalog while the splash is up (the
  // server answers from its boot-time warm cache), so shelves are stocked
  // the moment the player reaches any shop. As each store's products land we
  // also warm the browser image cache for their pictures (same <img> +
  // crossOrigin the surfaces use), so when an interior builds on approach the
  // texture bytes are already local instead of fading in a network hop later.
  void (async (): Promise<void> => {
    const imageUrls: string[] = [];
    await preloadAllProducts(
      shops.map((s: Shop) => s.id),
      async (shopId: string): Promise<ShopProduct[]> => {
        const products: ShopProduct[] = await fetchProducts(config.SERVER_URL, shopId);
        for (const product of products) {
          const url: string | undefined = product.images[0]?.url;
          if (url !== undefined && url !== "") {
            imageUrls.push(resizeShopifyImage(url, 512));
          }
        }
        return products;
      }
    );
    await preloadImageUrls(imageUrls);
  })();

  // Spawn at the shared spawn point (behind the north bench, facing the
  // escalators) while loading; the server-assigned scatter spot arriving via
  // StateSync refines it once room state lands.
  mall.controller.teleport(new Vector3(SPAWN_POSE.x, SPAWN_POSE.y, SPAWN_POSE.z), SPAWN_POSE.yaw);

  // Screen chrome: icon sidebar, category chips (wing teleports), player card.
  const goTo = (x: number, z: number, yaw: number, y: number = 0): void => {
    mall.controller.teleport(new Vector3(x, y, z), yaw);
  };
  // Wing chips are floor-aware: from the gallery level you land on that
  // wing's UPPER corridor entrance, from the ground on its ground entrance.
  const onUpperFloor = (): boolean => mall.controller.feet().y > 3.5;
  const goToWing = (x: number, z: number, yaw: number): void => {
    if (onUpperFloor()) {
      // Gallery inner edge sits 2m inside the atrium wall on the upper level.
      const inset: number = 33 / 30;
      goTo(x * inset, z * inset, yaw, 7);
    } else {
      goTo(x, z, yaw);
    }
  };
  hud.installChrome({
    onHome: () => goTo(0, 11, 0),
    onDirectory: () => {
      mall.releasePointerLock();
      statsPanel.toggle();
    },
    onCart: () => {
      mall.releasePointerLock();
      cartPanel.toggle();
    },
    onGiving: () => {
      window.open("https://berich.org", "_blank", "noopener,noreferrer");
    },
    categories: [
      { label: layout.wingTitles.north, onClick: () => goToWing(0, 30, 0) },
      { label: layout.wingTitles.east, onClick: () => goToWing(30, 0, Math.PI / 2) },
      { label: layout.wingTitles.west, onClick: () => goToWing(-30, 0, -Math.PI / 2) },
      // Single-story: always lands on the ground floor.
      { label: "Food Court", onClick: () => goTo(0, -30, Math.PI) },
      // North gallery, just off the escalator landing, facing back over
      // the atrium; downstairs returns to the spawn point.
      { label: "Upstairs", onClick: () => goTo(0, 32, Math.PI, 7) },
      { label: "Downstairs", onClick: () => goTo(0, 11, 0) },
    ],
  });

  new TouchJoystick("touch-joystick", "touch-joystick-knob", (x, y) => {
    mall.setJoystick(x, y);
  });
  new TouchLook(canvas, (dyaw: number, dpitch: number) => {
    mall.camera.rotation.y += dyaw;
    mall.camera.rotation.x = Math.max(
      -Math.PI / 2 + 0.05,
      Math.min(Math.PI / 2 - 0.05, mall.camera.rotation.x + dpitch)
    );
  });
  new TouchJoystick("look-joystick", "look-joystick-knob", (x, y) => {
    mall.setLookStick(x, y);
  });
  new JumpButton("jump-button", () => mall.jump());

  mall.start(() => {
    streaming.update(performance.now());
    const feet = mall.controller.feet();
    prefetcher.update(feet.x, feet.z);
  });

  // Dev debug hook — lets tests / console poke into scene internals
  (window as unknown as { __mallmart: unknown }).__mallmart = {
    mall, layout, shopUnits, carts, streaming,
  };

  const floorMeshes = [atrium, ...wingZones, foodCourt, ...shopUnits].flatMap((z) =>
    z.floorMeshes()
  );
  const xr: XRManager = new XRManager(mall.scene);
  await xr.init(floorMeshes);

  // World geometry is built; now the player's identity choice (made while
  // the mall loaded) gates the join.
  let session: Session | null = await sessionPromise;
  carts.setSession(session);
  const displayName: string = session !== null ? session.displayName : config.USERNAME;

  // Restore the signed-in shopper's persistent cart in the background.
  // NEVER await this before the join: right after a deploy the server's
  // all-store Shopify warm-up throttles cart reads for minutes, and gating
  // entry on it froze the splash for signed-in players while guests walked
  // straight in. A late restore is safe: CartManager.restore only fills
  // carts that are still empty, so anything added meanwhile wins.
  if (session !== null) {
    const authed: Session = session;
    void restorePersistentCart(httpBase(config.SERVER_URL), authed, carts, shops).catch(
      (err: unknown) => console.warn("[Mall Mart] Persistent cart restore failed:", err)
    );
  }
  startCartAutosave(httpBase(config.SERVER_URL), carts, () => {
    // Token expired mid-session: drop to guest for saves; next visit re-asks.
    clearSession();
    carts.setSession(null);
    hud.setStatus("Sign-in expired — cart changes no longer sync");
  });

  hud.setStatus(`Connecting to ${config.SERVER_URL}…`);
  const network: ColyseusClient = new ColyseusClient(config.SERVER_URL);
  let room;
  try {
    room = await network.join({
      username: displayName,
      ...(session !== null ? { email: session.email, token: session.token } : {}),
    });
  } catch (err: unknown) {
    if (isMallAtCapacity(err)) {
      splash.setReady(); // lift the overlay so the message is visible
      redirectAtCapacity(hud);
      return;
    }
    if (session !== null) {
      // Stale token rejected at the door: clear it and rejoin as guest
      // rather than dead-ending the visit.
      console.warn("[Mall Mart] Authenticated join failed; retrying as guest:", err);
      clearSession();
      carts.setSession(null);
      session = null;
      room = await network.join({ username: config.USERNAME });
    } else {
      throw err;
    }
  }
  const finalName: string = session !== null ? session.displayName : config.USERNAME;
  hud.setStatus(`${finalName} — welcome to Mall Mart`);
  // Sign out: drop the stored session and reload; the sign-in overlay is the
  // next thing a fresh boot shows. Reload also cleanly tears down the
  // authenticated Colyseus presence.
  hud.setPlayer(
    finalName,
    session !== null
      ? () => {
          clearSession();
          window.location.reload();
        }
      : undefined
  );

  const stateSync: StateSync = new StateSync(mall, room.sessionId, (x, y, z) => {
    mall.controller.teleport(new Vector3(x, y, z), SPAWN_POSE.yaw);
  });
  stateSync.attach(room);

  // Hold the loading screen until the scene AND its textures are genuinely
  // renderable, so nothing pops in after the overlay lifts.
  await mall.whenWorldReady((ready: number, total: number) => {
    splash.setProgress(`Loading Mall Mart… textures ${ready}/${total}`);
  });
  splash.setReady();

  const localPlayer: LocalPlayer = new LocalPlayer(mall.camera, network);
  localPlayer.start();

  // Deploys mid-session (and mobile's aggressive index.html caching) leave
  // clients on stale bundles; announce and force a refresh when the served
  // version moves on.
  startVersionWatch(httpBase(config.SERVER_URL), (reload) => {
    hud.setStatus("Mall Mart updated — refreshing…");
    reload();
  });
}

/** Matches the MALL_AT_CAPACITY marker MallRoom throws when the single mall
 *  room is full and a second create is refused. */
function isMallAtCapacity(err: unknown): boolean {
  return err instanceof Error && err.message.includes("MALL_AT_CAPACITY");
}

function redirectAtCapacity(hud: HUD): void {
  hud.setStatus("The mall is at capacity right now. Taking you to mart.club…");
  setTimeout(() => {
    window.location.href = "https://www.mart.club";
  }, 4000);
}

main().catch((err: Error) => {
  console.error("[Mall Mart] Fatal startup error:", err);
  // Drop the splash overlay so the error is actually visible.
  document.getElementById("splash")?.remove();
  const hud: HTMLElement | null = document.getElementById("hud");
  if (hud !== null) {
    hud.textContent = `Mall Mart — error: ${err.message}`;
  }
});
